/** Reserve 100% for exact completion while preserving whole-percent display elsewhere. */
export const displayProgressPercentage = (percentage: number): number => {
  if (!Number.isFinite(percentage)) return 0;
  const value = Math.max(0, Math.min(100, percentage));
  return value < 100 ? Math.min(99, Math.round(value)) : 100;
};

export type TriggerProgressCounts = {
  id?: string;
  triggerId?: string;
  name?: string;
  matchIds?: string[];
  expectedExecutions?: number;
  finishedExecutions?: number;
};

export const getTriggerMatchKeys = (trigger: TriggerProgressCounts): string[] => {
  if (trigger.matchIds?.length) {
    return [...trigger.matchIds];
  }
  const keys = [trigger.id, trigger.triggerId].filter((value): value is string => Boolean(value));
  return Array.from(new Set(keys));
};

export type EventTriggerKeysSource = {
  /** Legacy root field: may hold the schedule trigger id OR the original trigger definition id. */
  triggerId?: string | null;
  trigger?: {
    id?: string | null;
    triggerId?: string | null;
  } | null;
};

/**
 * Ids an event can be matched by. `event.triggerId` is legacy and may diverge from
 * `event.trigger.id` (the execution trigger doc id, which is what backend counters use),
 * so every known key is considered.
 */
export const getEventTriggerKeys = (event: EventTriggerKeysSource): string[] => {
  const keys = [event?.triggerId, event?.trigger?.id, event?.trigger?.triggerId].filter(
    (value): value is string => Boolean(value),
  );
  return Array.from(new Set(keys));
};

/** True when the event belongs to any of `triggerKeys` (empty keys means "no filter"). */
export const matchesTriggerKeys = (
  event: EventTriggerKeysSource,
  triggerKeys?: string[] | null,
): boolean => {
  if (!Array.isArray(triggerKeys) || triggerKeys.length === 0) {
    return true;
  }
  return getEventTriggerKeys(event).some(key => triggerKeys.includes(key));
};

/** Format `finished/expected`, or null when either count is missing / invalid. */
export const getTriggerProgressLabel = (
  trigger?: TriggerProgressCounts | null,
): string | null => {
  const finished = trigger?.finishedExecutions;
  const expected = trigger?.expectedExecutions;
  if (finished == null || expected == null || expected <= 0) {
    return null;
  }
  return `${finished}/${expected}`;
};

/**
 * Resolve a trigger from a list by id/triggerId (and matchIds), then by name.
 * Never aggregates — returns the single matching trigger or null.
 */
export const findMatchingTrigger = (
  triggers: TriggerProgressCounts[] | null | undefined,
  options: {
    triggerId?: string | null;
    name?: string | null;
  } = {},
): TriggerProgressCounts | null => {
  if (!Array.isArray(triggers) || triggers.length === 0) {
    return null;
  }

  const { triggerId, name } = options;

  if (triggerId) {
    const byId = triggers.find(trigger => getTriggerMatchKeys(trigger).includes(triggerId));
    if (byId) {
      return byId;
    }
  }

  const normalizedName = name?.trim();
  if (normalizedName) {
    const byName = triggers.find(trigger => trigger.name?.trim() === normalizedName);
    if (byName) {
      return byName;
    }
  }

  return null;
};

/** Progress label for one trigger matched from a live list, with denormalized fallback. */
export const getMatchingTriggerProgressLabel = (
  triggers?: TriggerProgressCounts[] | null,
  options: {
    triggerId?: string | null;
    name?: string | null;
    fallback?: TriggerProgressCounts | null;
  } = {},
): string | null => {
  const matched = findMatchingTrigger(triggers, {
    triggerId: options.triggerId,
    name: options.name ?? options.fallback?.name,
  });
  if (matched) {
    return getTriggerProgressLabel(matched);
  }

  return getTriggerProgressLabel(options.fallback);
};
